import React, { useState, useEffect, useCallback } from 'react';
import { Gift, Search, CreditCard, DollarSign, AlertTriangle, Eye, RefreshCw, XCircle, PlusCircle, ChevronDown, ArrowLeft, Clock, CheckCircle, MessageSquare, Mail, Phone, AlertCircle, Send, Package, Layers, Hash, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { giftcardApi } from '../../services/giftcardApi';
import { getBranchAuthHeaders } from '@/utils/branchApi';

const STATUS_COLORS = {
  active: 'bg-emerald-500/15 text-emerald-400', partially_redeemed: 'bg-amber-500/15 text-amber-400',
  redeemed: 'bg-gray-500/15 text-gray-400', expired: 'bg-red-500/15 text-red-400',
  voided: 'bg-red-500/15 text-red-400', pending_payment: 'bg-blue-500/15 text-blue-400',
  draft: 'bg-gray-500/15 text-gray-500', scheduled: 'bg-indigo-500/15 text-indigo-400',
};

export default function GiftCardPage() {
  const [view, setView] = useState('dashboard'); // dashboard | list | detail | physical
  const [summary, setSummary] = useState(null);
  const [activity, setActivity] = useState(null);
  const [cards, setCards] = useState({ items: [], total: 0 });
  const [cardsLoading, setCardsLoading] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [filters, setFilters] = useState({ status: '', search: '', page: 1 });
  const [loading, setLoading] = useState(false);
  const [settingsData, setSettingsData] = useState(null);
  const [tab, setTab] = useState('overview'); // overview | settings

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        giftcardApi.reportSummary(),
        giftcardApi.reportActivity(30),
      ]);
      setSummary(s);
      setActivity(a);
    } catch (e) { toast.error('Failed to load dashboard'); }
    setLoading(false);
  }, []);

  const loadCards = useCallback(async () => {
    setLoading(true);
    try {
      const res = await giftcardApi.list({ ...filters, page_size: 20 });
      setCards(res);
    } catch (e) { toast.error('Failed to load gift cards'); }
    setLoading(false);
  }, [filters]);

  const loadDetail = useCallback(async (id) => {
    try {
      const [card, txns] = await Promise.all([
        giftcardApi.detail(id),
        giftcardApi.transactions(id),
      ]);
      setSelectedCard(card);
      setTransactions(txns.transactions || []);
      try { const dlogs = await giftcardApi.deliveryLogs(id); setDeliveryLogs(dlogs.logs || []); } catch { setDeliveryLogs([]); }
      setView('detail');
    } catch (e) { toast.error('Failed to load details'); }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const s = await giftcardApi.getSettings();
      setSettingsData(s);
    } catch (e) { toast.error('Failed to load settings'); }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => { if (view === 'list') loadCards(); }, [view, loadCards]);

  const fmt = (n) => `$${(n || 0).toFixed(2)}`;
  const maskCode = (c) => c ? `${c.slice(0, 3)}****${c.slice(-4)}` : '';

  // ==================== DASHBOARD ====================
  if (view === 'dashboard') {
    return (
      <div className="space-y-6 p-4 sm:p-6 dark rounded-2xl bg-slate-950 text-slate-100" data-testid="gc-admin-dashboard">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Gift Cards</h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="border-white/10 text-gray-300 hover:bg-white/5"
              onClick={() => { setTab('settings'); loadSettings(); }} data-testid="gc-settings-btn">Settings</Button>
            <Button variant="outline" size="sm" className="border-white/10 text-gray-300 hover:bg-white/5"
              onClick={() => setView('physical')} data-testid="gc-physical-btn">
              <Package className="w-3.5 h-3.5 mr-1.5" /> Physical Cards
            </Button>
            <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
              onClick={() => { setView('list'); }} data-testid="gc-view-all">View All Cards</Button>
          </div>
        </div>

        {tab === 'settings' && settingsData && (
          <SettingsPanel data={settingsData} onClose={() => setTab('overview')} onSaved={loadSettings} />
        )}

        {tab === 'overview' && loading && !summary && (
          <div className="text-center py-12">
            <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
            <p className="text-gray-500 text-sm mt-3">Loading dashboard...</p>
          </div>
        )}

        {tab === 'overview' && summary && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={DollarSign} label="Total Issued" value={fmt(summary.total_issued_amount)} color="text-blue-400" bg="bg-blue-500/10" />
              <StatCard icon={CreditCard} label="Outstanding" value={fmt(summary.total_outstanding_balance)} color="text-emerald-400" bg="bg-emerald-500/10" sub={`${summary.total_active_cards} cards`} />
              <StatCard icon={CheckCircle} label="Redeemed" value={fmt(summary.total_redeemed_amount)} color="text-amber-400" bg="bg-amber-500/10" sub={`${summary.total_fully_redeemed} cards`} />
              <StatCard icon={AlertTriangle} label="Expired" value={fmt(summary.total_expired_amount)} color="text-red-400" bg="bg-red-500/10" sub={`${summary.total_expired_cards} cards`} />
            </div>
            {activity && (
              <div className="bg-white/[0.03] border border-white/5 rounded-xl p-5">
                <h3 className="text-white font-semibold mb-3">Last 30 Days</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div><p className="text-2xl font-bold text-white">{activity.total_sold}</p><p className="text-gray-500 text-xs">Sold</p></div>
                  <div><p className="text-2xl font-bold text-white">{activity.total_activated}</p><p className="text-gray-500 text-xs">Activated</p></div>
                  <div><p className="text-2xl font-bold text-white">{activity.total_redeemed}</p><p className="text-gray-500 text-xs">Redeemed</p></div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ==================== LIST ====================
  if (view === 'list') {
    return (
      <div className="space-y-4 p-4 sm:p-6 dark rounded-2xl bg-slate-950 text-slate-100" data-testid="gc-admin-list">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setView('dashboard')} className="text-gray-400 hover:text-white"><ArrowLeft className="w-5 h-5" /></button>
            <h2 className="text-xl font-bold text-white">Gift Cards ({cards.total})</h2>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-500" />
            <input placeholder="Search code, name, email..." value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value, page: 1 })}
              className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-4 py-2 text-white text-sm placeholder-gray-500 outline-none focus:border-white/20"
              data-testid="gc-search" />
          </div>
          <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value, page: 1 })}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" data-testid="gc-status-filter">
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="partially_redeemed">Partially Redeemed</option>
            <option value="redeemed">Redeemed</option>
            <option value="expired">Expired</option>
            <option value="voided">Voided</option>
            <option value="pending_payment">Pending</option>
          </select>
        </div>
        <div className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/5 text-gray-500 text-xs">
              <th className="text-left px-4 py-3">Code</th><th className="text-left px-4 py-3">Recipient</th>
              <th className="text-right px-4 py-3">Amount</th><th className="text-right px-4 py-3">Balance</th>
              <th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Delivery</th>
              <th className="text-left px-4 py-3">Date</th><th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {cards.items?.map(c => (
                <tr key={c.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] cursor-pointer" onClick={() => loadDetail(c.id)}>
                  <td className="px-4 py-3 text-white font-mono text-xs">{maskCode(c.code)}</td>
                  <td className="px-4 py-3 text-gray-300">{c.recipient_name || '-'}</td>
                  <td className="px-4 py-3 text-right text-white">{fmt(c.original_amount)}</td>
                  <td className="px-4 py-3 text-right text-white font-semibold">{fmt(c.current_balance)}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[c.status] || 'bg-gray-500/15 text-gray-400'}`}>{c.status?.replace('_', ' ')}</span></td>
                  <td className="px-4 py-3"><span className="text-xs text-gray-500 capitalize">{c.delivery_status?.replace('_', ' ')}</span></td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</td>
                  <td className="px-4 py-3"><Eye className="w-4 h-4 text-gray-600" /></td>
                </tr>
              ))}
              {(!cards.items || cards.items.length === 0) && !loading && (
                <tr><td colSpan={8} className="text-center py-8 text-gray-600">No gift cards found</td></tr>
              )}
              {loading && (
                <tr><td colSpan={8} className="text-center py-8"><div className="w-6 h-6 mx-auto border-2 border-white/20 border-t-amber-400 rounded-full animate-spin" /></td></tr>
              )}
            </tbody>
          </table>
        </div>
        {cards.total > 20 && (
          <div className="flex justify-center gap-2">
            <Button size="sm" variant="outline" disabled={filters.page <= 1} onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              className="border-white/10 text-gray-400">Prev</Button>
            <span className="text-gray-500 text-sm py-2">Page {filters.page}</span>
            <Button size="sm" variant="outline" disabled={filters.page * 20 >= cards.total} onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              className="border-white/10 text-gray-400">Next</Button>
          </div>
        )}
      </div>
    );
  }

  // ==================== DETAIL ====================
  if (view === 'detail' && selectedCard) {
    const c = selectedCard;
    const hasFailedDelivery = deliveryLogs.some(l => ['failed', 'undelivered'].includes(l.delivery_status));
    return (
      <div className="space-y-4 p-4 sm:p-6 dark rounded-2xl bg-slate-950 text-slate-100" data-testid="gc-admin-detail">
        <div className="flex items-center gap-3">
          <button onClick={() => { setView('list'); setSelectedCard(null); setDeliveryLogs([]); }} className="text-gray-400 hover:text-white"><ArrowLeft className="w-5 h-5" /></button>
          <h2 className="text-xl font-bold text-white">Gift Card Details</h2>
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="bg-white/[0.03] border border-white/5 rounded-xl p-5">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-white font-mono text-lg font-bold">{c.code}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[c.status]}`}>{c.status?.replace('_', ' ')}</span>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-white">{fmt(c.current_balance)}</p>
                <p className="text-gray-500 text-xs">of {fmt(c.original_amount)}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-gray-500 text-xs">Recipient</p><p className="text-white">{c.recipient_name || '-'}</p></div>
              <div><p className="text-gray-500 text-xs">Email</p><p className="text-white text-xs">{c.recipient_email || '-'}</p></div>
              <div><p className="text-gray-500 text-xs">Phone</p><p className="text-white text-xs">{c.recipient_phone || '-'}</p></div>
              <div><p className="text-gray-500 text-xs">Sender</p><p className="text-white">{c.sender_name || '-'}</p></div>
              <div><p className="text-gray-500 text-xs">Channel</p><p className="text-white capitalize">{c.source_channel?.replace('_', ' ')}</p></div>
              <div><p className="text-gray-500 text-xs">Type</p><p className="text-white capitalize">{c.gift_card_type}</p></div>
              {c.expires_at && <div className="col-span-2"><p className="text-gray-500 text-xs">Expires</p><p className="text-white">{new Date(c.expires_at).toLocaleDateString()}</p></div>}
            </div>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/5">
              <Button size="sm" onClick={async () => {
                try { await giftcardApi.resend({ code: c.code, delivery_type: 'recipient_delivery' }); toast.success('Recipient SMS/Email resent'); loadDetail(c.id); } catch (e) { toast.error(e.message); }
              }}
                className={`${hasFailedDelivery ? 'bg-amber-500/30 text-amber-300 hover:bg-amber-500/40' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'}`}
                data-testid="gc-resend-recipient">
                <Send className="w-3 h-3 mr-1" /> Resend to Recipient {hasFailedDelivery && '(Recommended)'}
              </Button>
              <Button size="sm" onClick={async () => {
                try { await giftcardApi.resend({ code: c.code, delivery_type: 'purchaser_confirmation' }); toast.success('Purchaser SMS resent'); loadDetail(c.id); } catch (e) { toast.error(e.message); }
              }}
                className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30" data-testid="gc-resend-purchaser">
                <RefreshCw className="w-3 h-3 mr-1" /> Resend to Purchaser
              </Button>
              {!['voided', 'redeemed'].includes(c.status) && (
                <Button size="sm" onClick={async () => { if (window.confirm('Void this card?')) { await giftcardApi.voidCard({ code: c.code, reason: 'Admin void' }); toast.success('Card voided'); loadDetail(c.id); }}}
                  className="bg-red-500/20 text-red-400 hover:bg-red-500/30" data-testid="gc-void"><XCircle className="w-3 h-3 mr-1" /> Void</Button>
              )}
            </div>
          </div>
          <div className="bg-white/[0.03] border border-white/5 rounded-xl p-5">
            <h3 className="text-white font-semibold mb-3">Transaction Ledger</h3>
            <div className="space-y-2 max-h-[250px] overflow-y-auto">
              {transactions.map(t => (
                <div key={t.id} className="flex items-center justify-between py-2 border-b border-white/[0.03] text-sm">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${t.transaction_type === 'redeem' ? 'bg-amber-400' : t.transaction_type === 'issue' ? 'bg-emerald-400' : t.transaction_type === 'void' ? 'bg-red-400' : 'bg-blue-400'}`} />
                    <span className="text-white capitalize">{t.transaction_type.replace('_', ' ')}</span>
                  </div>
                  <div className="text-right">
                    <span className={`font-mono ${t.transaction_type === 'redeem' ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {t.transaction_type === 'redeem' ? '-' : '+'}${t.amount.toFixed(2)}
                    </span>
                    <p className="text-gray-600 text-xs">{fmt(t.balance_after)}</p>
                  </div>
                </div>
              ))}
              {transactions.length === 0 && <p className="text-gray-600 text-sm">No transactions</p>}
            </div>
          </div>
        </div>
        {/* Delivery Logs Panel */}
        <DeliveryLogPanel logs={deliveryLogs} />
      </div>
    );
  }

  // ==================== PHYSICAL CARDS ====================
  if (view === 'physical') {
    return <PhysicalCardsView onBack={() => setView('dashboard')} />;
  }

  return null;
}

function PhysicalCardsView({ onBack }) {
  const [physTab, setPhysTab] = useState('inventory'); // inventory | generate | activate
  const [inventory, setInventory] = useState({ items: [], total: 0 });
  const [invLoading, setInvLoading] = useState(false);
  const [invFilters, setInvFilters] = useState({ batch_name: '', status: '', page: 1 });
  const [genForm, setGenForm] = useState({ batch_name: '', count: 10, denomination: 0 });
  const [generating, setGenerating] = useState(false);
  const [generatedBatch, setGeneratedBatch] = useState(null);
  const [actForm, setActForm] = useState({ card_number: '', amount: '' });
  const [activating, setActivating] = useState(false);
  const [activateResult, setActivateResult] = useState(null);

  const loadInventory = useCallback(async () => {
    setInvLoading(true);
    try {
      const params = { page: invFilters.page, page_size: 50 };
      if (invFilters.batch_name) params.batch_name = invFilters.batch_name;
      if (invFilters.status) params.status = invFilters.status;
      const res = await giftcardApi.physicalInventory(params);
      setInventory(res);
    } catch (e) { toast.error('Failed to load inventory'); }
    setInvLoading(false);
  }, [invFilters]);

  useEffect(() => { loadInventory(); }, [loadInventory]);

  const handleGenerate = async () => {
    if (!genForm.batch_name.trim()) { toast.error('Batch name is required'); return; }
    if (genForm.count < 1 || genForm.count > 500) { toast.error('Count must be 1-500'); return; }
    setGenerating(true);
    try {
      const res = await giftcardApi.physicalBulkGenerate({
        batch_name: genForm.batch_name.trim(),
        count: genForm.count, denomination: genForm.denomination,
      });
      setGeneratedBatch(res);
      toast.success(`Generated ${res.count} physical cards`);
      loadInventory();
    } catch (e) { toast.error(e.message || 'Failed to generate cards'); }
    setGenerating(false);
  };

  const handleActivate = async () => {
    if (!actForm.card_number.trim()) { toast.error('Card number is required'); return; }
    if (!actForm.amount || parseFloat(actForm.amount) <= 0) { toast.error('Amount must be positive'); return; }
    setActivating(true);
    try {
      const res = await giftcardApi.physicalActivate({
        card_number: actForm.card_number.trim(),
        amount: parseFloat(actForm.amount), activated_by: 'admin',
      });
      setActivateResult(res);
      toast.success(`Card activated with $${parseFloat(actForm.amount).toFixed(2)}`);
      setActForm({ card_number: '', amount: '' });
      loadInventory();
    } catch (e) { toast.error(e.message || 'Activation failed'); }
    setActivating(false);
  };

  const PHYS_STATUS_COLORS = {
    unassigned: 'bg-gray-500/15 text-gray-400',
    assigned: 'bg-blue-500/15 text-blue-400',
    active: 'bg-emerald-500/15 text-emerald-400',
    sold: 'bg-amber-500/15 text-amber-400',
    voided: 'bg-red-500/15 text-red-400',
    inactive: 'bg-gray-500/15 text-gray-500',
  };

  return (
    <div className="space-y-4 p-4 sm:p-6 dark rounded-2xl bg-slate-950 text-slate-100" data-testid="gc-physical-view">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-gray-400 hover:text-white" data-testid="gc-physical-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Package className="w-5 h-5 text-amber-400" /> Physical Gift Cards
          </h2>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 bg-white/[0.03] border border-white/5 rounded-lg p-1">
        {[
          { key: 'inventory', label: 'Inventory', icon: Layers },
          { key: 'generate', label: 'Generate Batch', icon: PlusCircle },
          { key: 'activate', label: 'Activate Card', icon: Zap },
        ].map(t => (
          <button key={t.key} onClick={() => setPhysTab(t.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              physTab === t.key ? 'bg-amber-500/20 text-amber-300' : 'text-gray-500 hover:text-gray-300 hover:bg-white/[0.03]'
            }`} data-testid={`phys-tab-${t.key}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Inventory Tab */}
      {physTab === 'inventory' && (
        <div className="space-y-3">
          <div className="flex gap-3 flex-wrap">
            <input placeholder="Filter by batch name..." value={invFilters.batch_name}
              onChange={e => setInvFilters({ ...invFilters, batch_name: e.target.value, page: 1 })}
              className="flex-1 min-w-[200px] bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 outline-none focus:border-white/20"
              data-testid="phys-filter-batch" />
            <select value={invFilters.status} onChange={e => setInvFilters({ ...invFilters, status: e.target.value, page: 1 })}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none"
              data-testid="phys-filter-status">
              <option value="">All Status</option>
              <option value="unassigned">Unassigned</option>
              <option value="active">Active</option>
              <option value="sold">Sold</option>
              <option value="voided">Voided</option>
            </select>
          </div>
          <div className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/5 text-gray-500 text-xs">
                <th className="text-left px-4 py-3">Card Number</th>
                <th className="text-left px-4 py-3">Printed Code</th>
                <th className="text-left px-4 py-3">Batch</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Activated</th>
                <th className="text-left px-4 py-3">Created</th>
              </tr></thead>
              <tbody>
                {inventory.items?.map(c => (
                  <tr key={c.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-white font-mono text-xs" data-testid={`phys-card-${c.id}`}>{c.card_number}</td>
                    <td className="px-4 py-3 text-gray-300 font-mono text-xs">{c.printed_code}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{c.batch_name}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${PHYS_STATUS_COLORS[c.status] || 'bg-gray-500/15 text-gray-400'}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{c.activated_at ? new Date(c.activated_at).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
                {(!inventory.items || inventory.items.length === 0) && !invLoading && (
                  <tr><td colSpan={6} className="text-center py-8 text-gray-600">No physical cards found</td></tr>
                )}
                {invLoading && (
                  <tr><td colSpan={6} className="text-center py-8"><div className="w-6 h-6 mx-auto border-2 border-white/20 border-t-amber-400 rounded-full animate-spin" /></td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-gray-600 text-xs text-right">{inventory.total} total cards</p>
          {inventory.total > 50 && (
            <div className="flex justify-center gap-2">
              <Button size="sm" variant="outline" disabled={invFilters.page <= 1} onClick={() => setInvFilters({ ...invFilters, page: invFilters.page - 1 })}
                className="border-white/10 text-gray-400">Prev</Button>
              <span className="text-gray-500 text-sm py-2">Page {invFilters.page}</span>
              <Button size="sm" variant="outline" disabled={invFilters.page * 50 >= inventory.total} onClick={() => setInvFilters({ ...invFilters, page: invFilters.page + 1 })}
                className="border-white/10 text-gray-400">Next</Button>
            </div>
          )}
        </div>
      )}

      {/* Generate Batch Tab */}
      {physTab === 'generate' && (
        <div className="max-w-lg space-y-4">
          <div className="bg-white/[0.03] border border-white/5 rounded-xl p-5 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <PlusCircle className="w-4 h-4 text-amber-400" /> Generate New Batch
            </h3>
            <div>
              <label className="text-gray-500 text-xs">Batch Name *</label>
              <input value={genForm.batch_name} onChange={e => setGenForm({ ...genForm, batch_name: e.target.value })}
                placeholder="e.g. Spring 2026 Promo"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm mt-1 outline-none focus:border-white/20"
                data-testid="phys-gen-batch-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-gray-500 text-xs">Number of Cards *</label>
                <input type="number" value={genForm.count} min={1} max={500}
                  onChange={e => setGenForm({ ...genForm, count: parseInt(e.target.value) || 0 })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm mt-1 outline-none focus:border-white/20"
                  data-testid="phys-gen-count" />
              </div>
              <div>
                <label className="text-gray-500 text-xs">Denomination ($)</label>
                <input type="number" value={genForm.denomination} min={0} step={5}
                  onChange={e => setGenForm({ ...genForm, denomination: parseFloat(e.target.value) || 0 })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm mt-1 outline-none focus:border-white/20"
                  data-testid="phys-gen-denomination" />
                <p className="text-gray-600 text-[10px] mt-1">$0 = variable load (any amount at activation)</p>
              </div>
            </div>
            <Button onClick={handleGenerate} disabled={generating}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold"
              data-testid="phys-gen-submit">
              {generating ? 'Generating...' : `Generate ${genForm.count} Cards`}
            </Button>
          </div>

          {generatedBatch && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-5" data-testid="phys-gen-result">
              <h4 className="text-emerald-400 font-semibold mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Batch Generated
              </h4>
              <p className="text-gray-300 text-sm mb-3">
                {generatedBatch.count} cards in "<span className="text-white">{generatedBatch.batch_name}</span>"
                {generatedBatch.denomination > 0 && <> at ${generatedBatch.denomination.toFixed(2)} each</>}
              </p>
              <div className="max-h-[200px] overflow-y-auto space-y-1">
                {generatedBatch.cards?.map((c, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 px-2 bg-white/[0.03] rounded text-xs">
                    <span className="text-white font-mono">{c.card_number}</span>
                    <span className="text-gray-500 font-mono">{c.printed_code}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Activate Card Tab */}
      {physTab === 'activate' && (
        <div className="max-w-lg space-y-4">
          <div className="bg-white/[0.03] border border-white/5 rounded-xl p-5 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" /> Activate Physical Card
            </h3>
            <p className="text-gray-500 text-sm">Scan or enter the card number / printed code / barcode to activate with a dollar amount.</p>
            <div>
              <label className="text-gray-500 text-xs">Card Number / Printed Code / Barcode *</label>
              <input value={actForm.card_number} onChange={e => setActForm({ ...actForm, card_number: e.target.value })}
                placeholder="e.g. 7029-XXXX-XXXX-XXXX or scan barcode"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm mt-1 font-mono outline-none focus:border-white/20"
                data-testid="phys-act-card-number" />
            </div>
            <div>
              <label className="text-gray-500 text-xs">Load Amount ($) *</label>
              <input type="number" value={actForm.amount} min={0.01} step={0.01}
                onChange={e => setActForm({ ...actForm, amount: e.target.value })}
                placeholder="50.00"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm mt-1 outline-none focus:border-white/20"
                data-testid="phys-act-amount" />
            </div>
            <Button onClick={handleActivate} disabled={activating}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
              data-testid="phys-act-submit">
              {activating ? 'Activating...' : 'Activate Card'}
            </Button>
          </div>

          {activateResult && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-5" data-testid="phys-act-result">
              <h4 className="text-emerald-400 font-semibold mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Card Activated
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-gray-500 text-xs">Card Number</p><p className="text-white font-mono">{activateResult.card_number}</p></div>
                <div><p className="text-gray-500 text-xs">Gift Card Code</p><p className="text-white font-mono">{activateResult.gift_card_code}</p></div>
                <div><p className="text-gray-500 text-xs">Amount Loaded</p><p className="text-white font-bold">${activateResult.amount?.toFixed(2)}</p></div>
                <div><p className="text-gray-500 text-xs">Balance</p><p className="text-emerald-400 font-bold">${activateResult.balance?.toFixed(2)}</p></div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, bg, sub }) {
  return (
    <div className={`${bg} border border-white/5 rounded-xl p-4`}>
      <div className="flex items-center gap-2 mb-2"><Icon className={`w-4 h-4 ${color}`} /><span className="text-gray-400 text-xs">{label}</span></div>
      <p className="text-white text-xl font-bold">{value}</p>
      {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
    </div>
  );
}

function DeliveryStatusBadge({ status }) {
  const cfg = {
    delivered: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', icon: CheckCircle, label: 'Delivered' },
    sent: { bg: 'bg-blue-500/15', text: 'text-blue-400', icon: Send, label: 'Sent' },
    queued: { bg: 'bg-indigo-500/15', text: 'text-indigo-400', icon: Clock, label: 'Queued' },
    accepted: { bg: 'bg-cyan-500/15', text: 'text-cyan-400', icon: Clock, label: 'Accepted' },
    pending: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: Clock, label: 'Pending' },
    failed: { bg: 'bg-red-500/15', text: 'text-red-400', icon: AlertCircle, label: 'Failed' },
    undelivered: { bg: 'bg-orange-500/15', text: 'text-orange-400', icon: AlertTriangle, label: 'Undelivered' },
  };
  const c = cfg[status] || cfg.pending;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`} data-testid={`badge-${status}`}>
      <Icon className="w-3 h-3" /> {c.label}
    </span>
  );
}

function DeliveryLogPanel({ logs }) {
  if (!logs || logs.length === 0) {
    return (
      <div className="bg-white/[0.03] border border-white/5 rounded-xl p-5" data-testid="delivery-log-panel">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-cyan-400" /> Delivery Log</h3>
        <p className="text-gray-600 text-sm">No delivery records</p>
      </div>
    );
  }

  const typeLabel = (t) => {
    if (t === 'purchaser_confirmation') return 'Purchaser Confirmation';
    if (t === 'recipient_delivery') return 'Recipient Delivery';
    return t || 'Delivery';
  };

  const channelIcon = (ch) => ch === 'sms' ? <Phone className="w-3.5 h-3.5 text-cyan-400" /> : <Mail className="w-3.5 h-3.5 text-amber-400" />;

  return (
    <div className="bg-white/[0.03] border border-white/5 rounded-xl p-5" data-testid="delivery-log-panel">
      <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-cyan-400" /> Delivery Log</h3>
      <div className="space-y-2">
        {logs.map((l) => (
          <div key={l.id} className="flex items-start justify-between py-3 px-3 border border-white/[0.04] rounded-lg bg-white/[0.01] hover:bg-white/[0.03] transition-colors" data-testid={`delivery-log-${l.id}`}>
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="mt-0.5">{channelIcon(l.delivery_channel)}</div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white text-sm font-medium">{typeLabel(l.delivery_type)}</span>
                  <DeliveryStatusBadge status={l.delivery_status} />
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  {l.delivery_channel?.toUpperCase()} to <span className="text-gray-400 font-mono">{l.destination}</span>
                  {l.provider_name && <span className="ml-2 text-gray-600">via {l.provider_name}</span>}
                </p>
                {(l.delivery_status === 'failed' || l.delivery_status === 'undelivered') && (l.error_code || l.error_message) && (
                  <p className="text-red-400/80 text-xs mt-1">
                    {l.error_code && <span className="font-mono mr-1">[{l.error_code}]</span>}
                    {l.error_message}
                  </p>
                )}
                {l.provider_message_id && (
                  <p className="text-gray-700 text-[10px] font-mono mt-1">ID: {l.provider_message_id}</p>
                )}
              </div>
            </div>
            <div className="text-right flex-shrink-0 ml-3">
              <p className="text-gray-500 text-xs">{l.created_at ? new Date(l.created_at).toLocaleString() : '-'}</p>
              {l.delivered_at && <p className="text-emerald-500/70 text-[10px]">Delivered {new Date(l.delivered_at).toLocaleTimeString()}</p>}
              {l.last_status_at && l.delivery_status !== 'delivered' && <p className="text-gray-600 text-[10px]">Updated {new Date(l.last_status_at).toLocaleTimeString()}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsPanel({ data, onClose, onSaved }) {
  const [form, setForm] = useState(data);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});

  const save = async () => {
    setSaving(true);
    try {
      await giftcardApi.updateSettings({
        enabled: form.enabled, allow_virtual: form.allow_virtual,
        custom_amount_enabled: form.custom_amount_enabled,
        custom_amount_min: form.custom_amount_min, custom_amount_max: form.custom_amount_max,
        expiration_enabled: form.expiration_enabled, expiration_days: form.expiration_days,
        allow_partial_redemption: form.allow_partial_redemption,
        allow_sms_delivery: form.allow_sms_delivery,
        send_purchaser_confirmation_sms: form.send_purchaser_confirmation_sms,
        send_recipient_giftcard_sms: form.send_recipient_giftcard_sms,
        code_prefix: form.code_prefix, code_length: form.code_length,
        terms_text: form.terms_text, support_contact: form.support_contact,
        brand_color: form.brand_color || null,
        storefront_hero_url: form.storefront_hero_url || null,
        storefront_logo_url: form.storefront_logo_url || null,
        storefront_tagline: form.storefront_tagline || null,
      });
      toast.success('Settings saved');
      onSaved?.();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleImageUpload = async (field, file) => {
    if (!file) return;
    setUploading(u => ({ ...u, [field]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const API = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${API}/api/uploads/image?folder=giftcard-branding`, { method: 'POST', body: fd, headers: getBranchAuthHeaders() });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Upload failed');
      const fullUrl = `${API}${data.url}`;
      setForm(f => ({ ...f, [field]: fullUrl }));
      toast.success('Image uploaded');
    } catch (e) { toast.error(e.message || 'Upload failed'); }
    setUploading(u => ({ ...u, [field]: false }));
  };

  const Toggle = ({ label, checked, onChange }) => (
    <label className="flex items-center justify-between py-2 cursor-pointer">
      <span className="text-gray-300 text-sm">{label}</span>
      <div className={`w-10 h-5 rounded-full relative transition-colors ${checked ? 'bg-emerald-500' : 'bg-gray-700'}`} onClick={onChange}>
        <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </div>
    </label>
  );

  const ImageUploadField = ({ label, field, hint }) => (
    <div className="pt-2">
      <label className="text-gray-500 text-xs">{label}</label>
      {hint && <p className="text-gray-600 text-[10px] mb-1">{hint}</p>}
      <div className="flex items-center gap-3 mt-1">
        {form[field] ? (
          <div className="relative group">
            <img src={form[field]} alt="" className="w-16 h-16 rounded-lg object-cover border border-white/10" />
            <button onClick={() => setForm(f => ({ ...f, [field]: null }))}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">x</button>
          </div>
        ) : (
          <div className="w-16 h-16 rounded-lg border border-dashed border-white/10 flex items-center justify-center text-gray-600 text-xs">
            No img
          </div>
        )}
        <label className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-colors ${uploading[field] ? 'bg-gray-700 text-gray-500' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}>
          {uploading[field] ? 'Uploading...' : 'Upload'}
          <input type="file" accept="image/*" className="hidden" disabled={uploading[field]}
            onChange={e => handleImageUpload(field, e.target.files[0])} data-testid={`gc-upload-${field}`} />
        </label>
      </div>
    </div>
  );

  return (
    <div className="bg-white/[0.03] border border-white/5 rounded-xl p-5" data-testid="gc-settings-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold">Gift Card Settings</h3>
        <button onClick={onClose} className="text-gray-500 hover:text-white text-sm">Close</button>
      </div>
      <div className="space-y-1">
        <Toggle label="Gift Cards Enabled" checked={form.enabled} onChange={() => setForm({ ...form, enabled: !form.enabled })} />
        <Toggle label="Allow Virtual" checked={form.allow_virtual} onChange={() => setForm({ ...form, allow_virtual: !form.allow_virtual })} />
        <Toggle label="Custom Amounts" checked={form.custom_amount_enabled} onChange={() => setForm({ ...form, custom_amount_enabled: !form.custom_amount_enabled })} />
        <Toggle label="Expiration" checked={form.expiration_enabled} onChange={() => setForm({ ...form, expiration_enabled: !form.expiration_enabled })} />
        <Toggle label="Partial Redemption" checked={form.allow_partial_redemption} onChange={() => setForm({ ...form, allow_partial_redemption: !form.allow_partial_redemption })} />
        <p className="text-gray-600 text-[10px] uppercase tracking-wider pt-3">SMS Delivery</p>
        <Toggle label="Enable SMS Delivery" checked={form.allow_sms_delivery} onChange={() => setForm({ ...form, allow_sms_delivery: !form.allow_sms_delivery })} />
        {form.allow_sms_delivery && (
          <>
            <Toggle label="Send Purchaser Confirmation SMS" checked={form.send_purchaser_confirmation_sms} onChange={() => setForm({ ...form, send_purchaser_confirmation_sms: !form.send_purchaser_confirmation_sms })} />
            <Toggle label="Send Recipient Gift Card SMS" checked={form.send_recipient_giftcard_sms} onChange={() => setForm({ ...form, send_recipient_giftcard_sms: !form.send_recipient_giftcard_sms })} />
          </>
        )}
        {form.custom_amount_enabled && (
          <div className="flex gap-3 pt-2">
            <div><label className="text-gray-500 text-xs">Min</label><input type="number" value={form.custom_amount_min} onChange={e => setForm({ ...form, custom_amount_min: parseFloat(e.target.value) })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm" /></div>
            <div><label className="text-gray-500 text-xs">Max</label><input type="number" value={form.custom_amount_max} onChange={e => setForm({ ...form, custom_amount_max: parseFloat(e.target.value) })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm" /></div>
          </div>
        )}
        {form.expiration_enabled && (
          <div className="pt-2"><label className="text-gray-500 text-xs">Expiration Days</label><input type="number" value={form.expiration_days || ''} onChange={e => setForm({ ...form, expiration_days: parseInt(e.target.value) || null })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm" /></div>
        )}
        <div className="pt-2"><label className="text-gray-500 text-xs">Code Prefix</label><input value={form.code_prefix} onChange={e => setForm({ ...form, code_prefix: e.target.value })}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm" /></div>

        {/* Storefront Branding */}
        <p className="text-gray-600 text-[10px] uppercase tracking-wider pt-4">Storefront Branding</p>
        <ImageUploadField label="Hero Banner" field="storefront_hero_url" hint="Recommended: 1200x400 landscape" />
        <ImageUploadField label="Logo" field="storefront_logo_url" hint="Square image, 200x200" />
        <div className="pt-2">
          <label className="text-gray-500 text-xs">Tagline</label>
          <input value={form.storefront_tagline || ''} onChange={e => setForm({ ...form, storefront_tagline: e.target.value })}
            placeholder="Give the gift of great food"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm mt-1" data-testid="gc-tagline-input" />
        </div>
        <div className="pt-2">
          <label className="text-gray-500 text-xs">Brand Color</label>
          <div className="flex items-center gap-2 mt-1">
            <input type="color" value={form.brand_color || '#f59e0b'}
              onChange={e => setForm({ ...form, brand_color: e.target.value })}
              className="w-8 h-8 rounded-lg border border-white/10 cursor-pointer bg-transparent" data-testid="gc-brand-color" />
            <input value={form.brand_color || ''} onChange={e => setForm({ ...form, brand_color: e.target.value })}
              placeholder="#f59e0b"
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm font-mono" />
            {form.brand_color && (
              <button onClick={() => setForm({ ...form, brand_color: null })} className="text-gray-500 hover:text-white text-xs">Reset</button>
            )}
          </div>
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="w-full mt-4 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="gc-settings-save">
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>
    </div>
  );
}
